import type {
  ActivateUserQueryVariables,
  AgreementsQueryVariables,
  CheckUnregisterQueryVariables,
  GetGlobalAgreementsQueryVariables,
  GetGlobalParentVerificationIsCompletedQueryVariables,
  PrepareAuthRequestInfoQueryVariables,
  SelfCertAgreementQueryVariables,
} from '../../graphql/generated/types';
import {ADAPTER_QUERY_KEY_PREFIX} from '../constant';

/**
 * Auth 도메인 Query Key 관리
 *
 * 기존 GraphQL generated types의 getKey 로직을 그대로 재사용
 */
export const AUTH_QUERY_KEY_MAP = {
  /**
   * 사용자 활성화
   * Origin: useActivateUserQuery.getKey
   */
  activateUser: (variables?: ActivateUserQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'activateUser'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'activateUser', variables] as const),

  /**
   * 약관 정보 조회
   * Origin: useAgreementsQuery.getKey
   */
  agreements: (variables?: AgreementsQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'agreements'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'agreements', variables] as const),
  /**
   * 회원 탈퇴 가능 여부 확인
   * Origin: useCheckUnregisterQuery.getKey
   */
  checkUnregister: (variables?: CheckUnregisterQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'checkUnregister'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'checkUnregister', variables] as const),
  /**
   * 글로벌 약관 정보 조회
   * Origin: useGetGlobalAgreementsQuery.getKey
   */
  getGlobalAgreements: (variables: GetGlobalAgreementsQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'getGlobalAgreements', variables] as const,
  /**
   * 글로벌 부모 인증 완료 여부 조회
   * Origin: useGetGlobalParentVerificationIsCompletedQuery.getKey
   */
  getGlobalParentVerificationIsCompleted: (
    variables: GetGlobalParentVerificationIsCompletedQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'getGlobalParentVerificationIsCompleted',
      variables,
    ] as const,
  /**
   * 본인 인증 약관 조회
   * Origin: useSelfCertAgreementQuery.getKey
   */
  selfCertAgreement: (variables: SelfCertAgreementQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'selfCertAgreement', variables] as const,
  /**
   * 본인인증 요청 정보 준비
   * Origin: usePrepareAuthRequestInfoQuery.getKey
   */
  prepareAuthRequestInfo: (variables: PrepareAuthRequestInfoQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'prepareAuthRequestInfo', variables] as const,
} as const;

/**
 * Auth 도메인 Mutation Key 관리
 */
export const AUTH_MUTATION_KEY_MAP = {
  /**
   * 이메일 비밀번호 변경
   * Origin: useChangeEmailPasswordMutation (getKey 없음)
   */
  changeEmailPassword: 'changeEmailPassword',
  /**
   * 사용자 탈퇴
   * Origin: useUnregisterUserMutation.getKey
   */
  unregisterUser: 'unregisterUser',
  /**
   * 이메일 인증 코드 확인
   * Origin: useCheckEmailVerifyCodeMutation (getKey 없음)
   */
  checkEmailVerifyCode: 'checkEmailVerifyCode',
  /**
   * 글로벌 부모 인증 확인
   * Origin: useGlobalCheckParentVerifyMutation (getKey 없음)
   */
  globalCheckParentVerify: 'globalCheckParentVerify',
  /**
   * 글로벌 이메일 회원가입
   * Origin: useGlobalEmailSignupMutation (getKey 없음)
   */
  globalEmailSignup: 'globalEmailSignup',
  /**
   * 글로벌 개인정보 저장
   * Origin: useGlobalSavePrivateMutation (getKey 없음)
   */
  globalSavePrivate: 'globalSavePrivate',
  /**
   * 글로벌 부모 인증 전송
   * Origin: useGlobalSendParentVerifyMutation (getKey 없음)
   */
  globalSendParentVerify: 'globalSendParentVerify',
  /**
   * 글로벌 SNS 회원가입
   * Origin: useGlobalSnsSignupMutation (getKey 없음)
   */
  globalSnsSignup: 'globalSnsSignup',
  /**
  /**
   * 본인 인증 정보 저장
   * Origin: useSaveAuthInfoMutation (getKey 없음)
   */
  saveAuthInfo: 'saveAuthInfo',
  /**
   * 이메일 인증 코드 전송
   * Origin: useSendEmailVerifyCodeMutation (getKey 없음)
   */
  sendEmailVerifyCode: 'sendEmailVerifyCode',
  /**
   * 글로벌 약관 설정
   * Origin: useSetGlobalAgreementMutation (getKey 없음)
   */
  setGlobalAgreement: 'setGlobalAgreement',
  /**
   * 카카오뱅크 본인인증 지원 기기 확인
   * Origin: useKakaoBankSelfCertSupportedDeviceMutation (getKey 없음)
   * ⚠️ Mutation으로 명명되어 있지만 GET 메소드 사용
   */
  kakaoBankSelfCertSupportedDevice: 'kakaoBankSelfCertSupportedDevice',
} as const;
